// Volume rendering module: create/destroy/update colormap uniforms.
//
// params contract — keys read from the reactive store:
//   colormapMin, colormapMax, transparentBounds
//   volumeEnabled, volumeSteps, volumeDensity, volumeBrightness

import * as THREE from 'three';
import { physicalToThreeJS } from '@adapters/coordinates';
import { strengthToColor } from '@physics/colormap';
import { reattachOrphanedSlices } from './slices';
import { RENDER_ORDER } from './renderOrder';
import volumeVert from '@shaders/volume.vert.glsl?raw';
import volumeFrag from '@shaders/volume.frag.glsl?raw';
import { resolveDomainBounds } from '@store/selections';
import type { ViewerContext } from '@app/context';
import type { ParamsState } from '@store/schema';
import type { ColorRGBA } from '@physics/types';

type StrengthToColor = (s: number) => ColorRGBA;

interface CreateVolumeCtx {
    THREE: typeof import('three');
    scene: THREE.Scene;
    GRID_X_MIN: number; GRID_X_MAX: number;
    GRID_Y_MIN: number; GRID_Y_MAX: number;
    GRID_Z_MIN: number; GRID_Z_MAX: number;
    FIELD_NX: number; FIELD_NY: number; FIELD_NZ: number;
    physicalToThreeJS: (x: number, y: number, z: number) => THREE.Vector3;
    getCurrentFieldData: () => Float32Array | null | undefined;
    getVolumeFieldData?: () => Float32Array | null | undefined;
    fieldStrengthToColor: StrengthToColor;
    params: ParamsState;
}

interface CreateVolumeResult {
    volumeMesh: THREE.Mesh | null;
    volumeMaterial: THREE.ShaderMaterial | null;
    volumeScalarMin: number | null;
    volumeScalarMax: number | null;
}

// Aliased to avoid stale @types/three references; DataTexture3D exists at runtime.
type DataTexture3DCtor = new (data: ArrayBufferView, w: number, h: number, d: number) => THREE.Data3DTexture;

export function createVolume(ctx: CreateVolumeCtx): CreateVolumeResult {
    const {
        THREE,
        scene,
        GRID_X_MIN, GRID_X_MAX, GRID_Y_MIN, GRID_Y_MAX, GRID_Z_MIN, GRID_Z_MAX,
        FIELD_NX, FIELD_NY, FIELD_NZ,
        physicalToThreeJS,
        getCurrentFieldData,
        getVolumeFieldData,
        fieldStrengthToColor,
        params,
    } = ctx;

    const xRange = GRID_X_MAX - GRID_X_MIN;
    const yRange = GRID_Y_MAX - GRID_Y_MIN;
    const zRange = GRID_Z_MAX - GRID_Z_MIN;
    const nx = FIELD_NX, ny = FIELD_NY, nz = FIELD_NZ;
    const dataSelector = (typeof getVolumeFieldData === 'function') ? getVolumeFieldData : getCurrentFieldData;
    const scalarArray = dataSelector();
    if (!scalarArray || nx * ny * nz !== scalarArray.length) {
        console.warn('Volume: scalar field not ready or wrong size');
        return { volumeMesh: null, volumeMaterial: null, volumeScalarMin: null, volumeScalarMax: null };
    }

    let fieldMin = Infinity;
    let fieldMax = -Infinity;
    for (let i = 0, n = scalarArray.length; i < n; i++) {
        const v = scalarArray[i] ?? 0;
        if (v < fieldMin) fieldMin = v;
        if (v > fieldMax) fieldMax = v;
    }
    const range = (fieldMax - fieldMin) || 1;

    // Pack into 3D texture with mapping: object (x,y,z) = (physical Y, physical Z, physical X)
    const data = new Uint8Array(scalarArray.length);
    for (let i = 0; i < nx; i++) {
        for (let j = 0; j < ny; j++) {
            for (let k = 0; k < nz; k++) {
                const idxSrc = i * (ny * nz) + j * nz + k;
                let norm = ((scalarArray[idxSrc] ?? 0) - fieldMin) / range;
                if (norm < 0) norm = 0; else if (norm > 1) norm = 1;
                const idxDst = j + ny * (k + nz * i);
                data[idxDst] = (norm * 255) | 0;
            }
        }
    }

    // DataTexture3D in older three versions; Data3DTexture in newer. Pick whichever exists.
    const Tex3DCtor: DataTexture3DCtor = ((THREE as unknown as { DataTexture3D?: DataTexture3DCtor }).DataTexture3D
        ?? (THREE as unknown as { Data3DTexture: DataTexture3DCtor }).Data3DTexture);
    const tex3d = new Tex3DCtor(data, ny, nz, nx);
    tex3d.format = THREE.RedFormat;
    tex3d.type = THREE.UnsignedByteType;
    tex3d.unpackAlignment = 1;
    tex3d.minFilter = THREE.LinearFilter;
    tex3d.magFilter = THREE.LinearFilter;
    tex3d.wrapS = THREE.ClampToEdgeWrapping;
    tex3d.wrapT = THREE.ClampToEdgeWrapping;
    tex3d.wrapR = THREE.ClampToEdgeWrapping;
    tex3d.needsUpdate = true;

    // Colormap 1D texture
    const cmapCanvas = document.createElement('canvas');
    cmapCanvas.width = 256; cmapCanvas.height = 1;
    const cmapCtx = cmapCanvas.getContext('2d');
    if (cmapCtx) {
        const cmapImg = cmapCtx.createImageData(256, 1);
        for (let i = 0; i < 256; i++) {
            const val = fieldMin + (i / 255) * range;
            const c = fieldStrengthToColor(val);
            cmapImg.data[i * 4 + 0] = c.r;
            cmapImg.data[i * 4 + 1] = c.g;
            cmapImg.data[i * 4 + 2] = c.b;
            cmapImg.data[i * 4 + 3] = 255;
        }
        cmapCtx.putImageData(cmapImg, 0, 0);
    }
    const cmapTex = new THREE.CanvasTexture(cmapCanvas);
    cmapTex.minFilter = THREE.LinearFilter;
    cmapTex.magFilter = THREE.LinearFilter;
    cmapTex.wrapS = THREE.ClampToEdgeWrapping;
    cmapTex.wrapT = THREE.ClampToEdgeWrapping;

    const volumeMaterial = new THREE.ShaderMaterial({
        vertexShader: volumeVert,
        fragmentShader: volumeFrag,
        transparent: true,
        depthWrite: false,
        side: THREE.DoubleSide,
        glslVersion: THREE.GLSL3,
        uniforms: {
            uVolume: { value: tex3d },
            uColormapTex: { value: cmapTex },
            uBoxMin: { value: new THREE.Vector3(-yRange / 2, -zRange / 2, -xRange / 2) },
            uBoxMax: { value: new THREE.Vector3(yRange / 2, zRange / 2, xRange / 2) },
            uSteps: { value: (typeof params.volumeSteps === 'number' ? params.volumeSteps : 64.0) },
            uDensity: { value: (typeof params.volumeDensity === 'number' ? params.volumeDensity : 8.0) },
            uBrightness: { value: (typeof params.volumeBrightness === 'number' ? params.volumeBrightness : 1.0) },
            uInvModelMatrix: { value: new THREE.Matrix4() },
            uTransparentBounds: { value: params.transparentBounds ? 1.0 : 0.0 },
            uColorMinNorm: { value: 0.0 },
            uColorMaxNorm: { value: 1.0 },
            // Stage 5 domain clip — values are in normalized 0..1 uvw space.
            // `applyVolumeDomainClip` repacks these from `params.volumeDomain` +
            // grid bounds. Default is "no clip".
            uClipMode: { value: 0 },
            uClipBoxMin: { value: new THREE.Vector3(0, 0, 0) },
            uClipBoxMax: { value: new THREE.Vector3(1, 1, 1) },
            uClipSphereCenter: { value: new THREE.Vector3(0.5, 0.5, 0.5) },
            uClipSphereExtent: { value: new THREE.Vector3(yRange, zRange, xRange) },
            uClipSphereRadius: { value: 0.5 },
        },
    });

    const geomBox = new THREE.BoxGeometry(yRange, zRange, xRange);
    const volumeMesh = new THREE.Mesh(geomBox, volumeMaterial);
    volumeMesh.renderOrder = RENDER_ORDER.VOLUME;
    volumeMesh.frustumCulled = false;
    const centerThree = physicalToThreeJS((GRID_X_MIN + GRID_X_MAX) / 2, (GRID_Y_MIN + GRID_Y_MAX) / 2, (GRID_Z_MIN + GRID_Z_MAX) / 2);
    volumeMesh.position.copy(centerThree);
    volumeMesh.onBeforeRender = () => {
        const u = volumeMaterial.uniforms['uInvModelMatrix'];
        if (u) (u.value as THREE.Matrix4).copy(volumeMesh.matrixWorld).invert();
    };
    scene.add(volumeMesh);

    return { volumeMesh, volumeMaterial, volumeScalarMin: fieldMin, volumeScalarMax: fieldMax };
}

interface DestroyVolumeCtx {
    scene: THREE.Scene | null;
    volumeMesh: THREE.Mesh | null;
    volumeMaterial: THREE.Material | null;
}

export function destroyVolume(ctx: DestroyVolumeCtx): void {
    const { scene, volumeMesh, volumeMaterial } = ctx;
    if (volumeMesh && volumeMesh.parent && scene) scene.remove(volumeMesh);
    if (volumeMaterial) {
        const shaderMat = volumeMaterial as THREE.ShaderMaterial;
        if (shaderMat.uniforms) {
            const u = shaderMat.uniforms;
            const cmap = u['uColormapTex'];
            if (cmap && (cmap.value as THREE.Texture)?.dispose) (cmap.value as THREE.Texture).dispose();
            const vol = u['uVolume'];
            if (vol && (vol.value as THREE.Texture)?.dispose) (vol.value as THREE.Texture).dispose();
        }
        if (volumeMaterial.dispose) volumeMaterial.dispose();
    }
}

/**
 * Tear down the active volume on viewer and reattach any slice meshes
 * that had been orphaned. Idempotent — safe to call when no volume exists.
 */
export function teardownVolume(viewer: ViewerContext): void {
    const vol = viewer.objects.volume;
    destroyVolume({ scene: viewer.three.scene, volumeMesh: vol.mesh, volumeMaterial: vol.material });
    vol.mesh = null;
    vol.material = null;
    reattachOrphanedSlices(viewer);
}

interface UpdateColormapCtx {
    THREE: typeof import('three');
    volumeMaterial: THREE.ShaderMaterial | null;
    volumeScalarMin: number | null;
    volumeScalarMax: number | null;
    fieldStrengthToColor: StrengthToColor;
    /** Display range, transparent-bounds flag — sourced from a ColormapBinding. */
    colormapMin: number;
    colormapMax: number;
    transparentBounds: boolean;
}

export function updateVolumeColormap(ctx: UpdateColormapCtx): void {
    const { THREE, volumeMaterial, volumeScalarMin, volumeScalarMax, fieldStrengthToColor, colormapMin, colormapMax, transparentBounds } = ctx;
    if (!volumeMaterial || !volumeMaterial.uniforms || !volumeMaterial.uniforms['uColormapTex']) return;
    const fieldMin = volumeScalarMin;
    const fieldMax = volumeScalarMax;
    if (fieldMin === null || fieldMax === null) return;
    const range = (fieldMax - fieldMin) || 1;

    const cmapCanvas = document.createElement('canvas');
    cmapCanvas.width = 256; cmapCanvas.height = 1;
    const cmapCtx = cmapCanvas.getContext('2d');
    if (cmapCtx) {
        const cmapImg = cmapCtx.createImageData(256, 1);
        for (let i = 0; i < 256; i++) {
            const val = fieldMin + (i / 255) * range;
            const c = fieldStrengthToColor(val);
            cmapImg.data[i * 4 + 0] = c.r;
            cmapImg.data[i * 4 + 1] = c.g;
            cmapImg.data[i * 4 + 2] = c.b;
            cmapImg.data[i * 4 + 3] = 255;
        }
        cmapCtx.putImageData(cmapImg, 0, 0);
    }
    const newCmap = new THREE.CanvasTexture(cmapCanvas);
    newCmap.minFilter = THREE.LinearFilter; newCmap.magFilter = THREE.LinearFilter;
    newCmap.wrapS = THREE.ClampToEdgeWrapping; newCmap.wrapT = THREE.ClampToEdgeWrapping;
    const cmapUniform = volumeMaterial.uniforms['uColormapTex'];
    const oldTex = cmapUniform?.value as THREE.Texture | undefined;
    if (cmapUniform) cmapUniform.value = newCmap;
    if (oldTex && oldTex.dispose) oldTex.dispose();

    const tbUniform = volumeMaterial.uniforms['uTransparentBounds'];
    if (tbUniform) tbUniform.value = transparentBounds ? 1.0 : 0.0;
    const minU = volumeMaterial.uniforms['uColorMinNorm'];
    const maxU = volumeMaterial.uniforms['uColorMaxNorm'];
    if (minU && maxU) {
        minU.value = Math.max(0.0, Math.min(1.0, (colormapMin - fieldMin) / range));
        maxU.value = Math.max(0.0, Math.min(1.0, (colormapMax - fieldMin) / range));
    }
}

/**
 * Tear down + (optionally) rebuild the volume mesh.
 */
export function syncVolume(viewer: ViewerContext, params: ParamsState): void {
    teardownVolume(viewer);

    if (!params.volumeEnabled) return;

    const grid = viewer.grid;
    const scene = viewer.three.scene;
    if (!scene) return;
    const vol = viewer.objects.volume;

    const getCurrentFieldData = (): Float32Array | null | undefined => {
        const f = viewer.field;
        return (f.currentComponent === 'magnitude') ? f.gridFlat : (f.data ? f.data[f.currentComponent] : null);
    };
    const out = createVolume({
        THREE,
        scene,
        GRID_X_MIN: grid.xMin, GRID_X_MAX: grid.xMax, GRID_Y_MIN: grid.yMin, GRID_Y_MAX: grid.yMax, GRID_Z_MIN: grid.zMin, GRID_Z_MAX: grid.zMax,
        FIELD_NX: grid.nx, FIELD_NY: grid.ny, FIELD_NZ: grid.nz,
        physicalToThreeJS,
        getCurrentFieldData,
        getVolumeFieldData: getCurrentFieldData,
        fieldStrengthToColor: (s: number) => strengthToColor(viewer, params, s),
        params,
    });
    vol.mesh = out.volumeMesh;
    vol.material = out.volumeMaterial;
    vol.scalarMin = out.volumeScalarMin ?? 0;
    vol.scalarMax = out.volumeScalarMax ?? 1;

    const u = (vol.material as THREE.ShaderMaterial | null)?.uniforms;
    if (u && u['uTransparentBounds']) {
        u['uTransparentBounds'].value = params.transparentBounds ? 1.0 : 0.0;
    }

    try {
        updateVolumeColormap({
            THREE, volumeMaterial: vol.material as THREE.ShaderMaterial | null,
            volumeScalarMin: vol.scalarMin, volumeScalarMax: vol.scalarMax,
            fieldStrengthToColor: (s: number) => strengthToColor(viewer, params, s),
            colormapMin: params.colormapMin, colormapMax: params.colormapMax,
            transparentBounds: !!params.transparentBounds,
        });
    } catch { /* swallow */ }

    // Apply any active domain clip — the selections intent handler also
    // calls this on set-domain, but syncVolume can be invoked from many
    // paths (toggle, dataset switch) so we keep it idempotent here.
    try { applyVolumeDomainClip(viewer, params); } catch { /* swallow */ }
}

/**
 * Push the current `params.volumeDomain` selection onto the volume's clip
 * uniforms. Idempotent — call after either the domain or the registry
 * itself changes. Coord-axis remap inside: the volume texture's uvw axes
 * are (physical Y, physical Z, physical X), so we reorder physical bounds
 * accordingly.
 */
export function applyVolumeDomainClip(viewer: ViewerContext, params: ParamsState): void {
    const mat = viewer.objects.volume.material as THREE.ShaderMaterial | null;
    if (!mat || !mat.uniforms) return;
    const u = mat.uniforms;

    const domain = resolveDomainBounds(params.selections, params.volumeDomain);
    const g = viewer.grid;
    const xRange = (g.xMax - g.xMin) || 1;
    const yRange = (g.yMax - g.yMin) || 1;
    const zRange = (g.zMax - g.zMin) || 1;

    if (domain.kind === 'full') {
        if (u['uClipMode']) u['uClipMode']!.value = 0;
        return;
    }
    if (domain.kind === 'box') {
        // Remap physical (x,y,z) → uvw (Y, Z, X).
        const minU = (domain.lower[1] - g.yMin) / yRange;
        const minV = (domain.lower[2] - g.zMin) / zRange;
        const minW = (domain.lower[0] - g.xMin) / xRange;
        const maxU = (domain.upper[1] - g.yMin) / yRange;
        const maxV = (domain.upper[2] - g.zMin) / zRange;
        const maxW = (domain.upper[0] - g.xMin) / xRange;
        if (u['uClipMode']) u['uClipMode']!.value = 1;
        if (u['uClipBoxMin']) (u['uClipBoxMin']!.value as THREE.Vector3).set(minU, minV, minW);
        if (u['uClipBoxMax']) (u['uClipBoxMax']!.value as THREE.Vector3).set(maxU, maxV, maxW);
        return;
    }
    // sphere
    const cU = (domain.center[1] - g.yMin) / yRange;
    const cV = (domain.center[2] - g.zMin) / zRange;
    const cW = (domain.center[0] - g.xMin) / xRange;
    if (u['uClipMode']) u['uClipMode']!.value = (domain.keep === 'inside') ? 2 : 3;
    if (u['uClipSphereCenter']) (u['uClipSphereCenter']!.value as THREE.Vector3).set(cU, cV, cW);
    if (u['uClipSphereExtent']) (u['uClipSphereExtent']!.value as THREE.Vector3).set(yRange, zRange, xRange);
    if (u['uClipSphereRadius']) u['uClipSphereRadius']!.value = domain.radius;
}

/**
 * Toggle transparent-bounds on the volume's shader uniforms.
 */
export function applyTransparentBounds(viewer: ViewerContext, params: ParamsState, value: boolean): void {
    const vol = viewer.objects.volume;
    const mat = vol.material as THREE.ShaderMaterial | null;
    if (!params.volumeEnabled || !mat || !mat.uniforms) return;
    const u = mat.uniforms;
    if (u['uTransparentBounds']) {
        u['uTransparentBounds'].value = value ? 1.0 : 0.0;
    }
    if (vol.scalarMin !== null && vol.scalarMax !== null
        && u['uColorMinNorm'] && u['uColorMaxNorm']) {
        const range = (vol.scalarMax - vol.scalarMin) || 1;
        u['uColorMinNorm'].value = Math.max(0.0, Math.min(1.0, (params.colormapMin - vol.scalarMin) / range));
        u['uColorMaxNorm'].value = Math.max(0.0, Math.min(1.0, (params.colormapMax - vol.scalarMin) / range));
    }
}
